import React, { useEffect, useState } from 'react';
import MultilineArrayInput from '../cards/multiline-input';
import { useTranslation } from 'next-i18next';
import { useGetWordListTemplateQuery, useUpdateWordListTemplateMutation } from '@app/store/organization/api';
import { useRouter } from 'next/router';
import { organizationWordTemplateRequest } from '@app/store/organization/types';
import Button from '../ui/button';
import { useModal } from '../modal-views/context';
import { toast } from 'react-toastify';
import { ToastId } from '@app/constants/ToastId';
import { useGetProjectsInOrganizationQuery } from '@app/store/project/api';
import SearchInput from '../ui/input/search-input';
import cn from 'classnames';
import NonAssignedProject from '../datatable/project-table/non-assigned-projects';
import { useAppSelector } from '@app/store/hooks';
import { selectAssignedProjectInfo } from '@app/store/project/assign-projects-slice';
import { RadioGroup, RadioGroupItem } from '../shadcn-ui/radio-group';
import { Label } from '../shadcn-ui/label';
import { configurationRequestBody } from '@app/store/config/types';
import { RouteTwoTone } from '@mui/icons-material';
import { usePutWhitelistMutation, usePutBlacklistMutation } from '@app/store/config/api';

function AddDetailsToOrganizationWordListTemplate({ templateId }: { templateId: string }) {
    const { t } = useTranslation(['common', 'organization']);
    const [wordLists, setWordLists] = useState<Array<string>>([]);
    const router = useRouter();
    const { closeModal } = useModal();

    const {
        data: wordlistTemplate,
        isLoading: isWordlistTemplateLoading,
        isSuccess: isWordlistTemplateLoaded
    } = useGetWordListTemplateQuery({
        organizationId: router.query.organizationId as string,
        wordListTemplateId: templateId
    });

    const [putWhitelistFile] = usePutWhitelistMutation();
    const [putBlacklistFile] = usePutBlacklistMutation();
    const [listType, setListType] = useState<'blacklist' | 'whitelist'>('blacklist');

    const [updateWordListTemplate, { isLoading: isUpdating }] = useUpdateWordListTemplateMutation();
    const [searchQuery, setSearchQuery] = useState('');
    const { data: allProjects, isLoading } = useGetProjectsInOrganizationQuery({
        organizationId: router.query.organizationId as string,
        query: searchQuery
    });

    const handleSearch = (event: any) => {
        event.preventDefault();
        const query = event.target.value.trim();
        setSearchQuery(query);
    };

    useEffect(() => {
        if (isWordlistTemplateLoaded) {
            setWordLists(wordlistTemplate?.words || []);
        }
    }, [isWordlistTemplateLoaded, wordlistTemplate]);

    const handleSaveChanges = async () => {
        try {
            const wordListRequestBody: organizationWordTemplateRequest = {
                organizationId: router.query.organizationId as string,
                templateId: templateId,
                wordListName: wordlistTemplate?.wordListName as string,
                words: wordLists
            };

            await updateWordListTemplate(wordListRequestBody).unwrap();
            toast.success(`${t('toast.wordListTemplatesaved')}`, { toastId: ToastId.WORDLIST_SAVED_TOAST });

            if (selectedProject.selectedRows.length > 0) {
                await Promise.all(
                    selectedProject.selectedRows.map(async (project) => {
                        const requestBody: configurationRequestBody = {
                            organizationId: router.query.organizationId as string,
                            projectId: project.projectId,
                            body: wordLists
                        };

                        if (listType === 'blacklist') {
                            await putBlacklistFile(requestBody).unwrap();
                        } else {
                            await putWhitelistFile(requestBody).unwrap();
                        }
                    })
                );
            }

            closeModal();
        } catch (err) {
            console.error(err);
            toast.error(`${t('toast.wordListTemplateUpdatingError')}`, { toastId: ToastId.WORDLIST_ERROR_TOAST });
        }
    };

    const handleCancel = (e: any) => {
        e.preventDefault();
        closeModal();
    };
    const selectedProject = useAppSelector(selectAssignedProjectInfo);

    const isProjectSelected = selectedProject.selectedRows.length > 0;

    return (
        <div className="flex w-[90vw] max-w-xl flex-col p-4">
            <h1 className={'pb-2 text-xl font-bold'}>{wordlistTemplate?.wordListName}</h1>
            <MultilineArrayInput value={wordLists?.join('\n')} setValue={(value: string[]) => setWordLists(value)} isDisabled={isWordlistTemplateLoading} inputPlaceholder={t('enterWords', { ns: 'organization' })} />
            <div className="z-50 flex items-center gap-2 ">
                {t('replace', { ns: 'organization' })}
                <RadioGroup defaultValue="blacklist" className="flex" onValueChange={(value) => setListType(value as 'blacklist' | 'whitelist')}>
                    <div className="flex items-center space-x-2">
                        <RadioGroupItem value="blacklist" id="blacklist" />
                        <Label htmlFor="blacklist">Blacklist</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <RadioGroupItem value="whitelist" id="whitelist" />
                        <Label htmlFor="whitelist">Whitelist</Label>
                    </div>
                </RadioGroup>
                {t('inProjects', { ns: 'organization' })}
            </div>

            <div className="relative w-full">
                <div className={cn('absolute left-0 right-0 top-0 flex w-full items-center justify-between rounded-xl bg-white py-1.5', isProjectSelected ? 'z-0 ' : 'z-10')}>
                    <SearchInput id="project-search" placeholder={t('searchProject', { ns: 'organization' })} handleSearch={handleSearch} fullWidth />
                </div>
            </div>
            <NonAssignedProject data={allProjects?.nonDeletedProjects} isLoading={isLoading} fromWordListTemplate />
            <div className={'ml-auto flex w-1/2 items-center justify-end gap-2 pt-2'}>
                <Button
                    data-testid="cancel-button"
                    onClick={handleCancel}
                    className={'flex-1 !border-gray-300 !bg-white !text-black-1 hover:!bg-gray-50'}
                    variant={'outline'}
                    isLoading={isWordlistTemplateLoading}
                    disabled={isWordlistTemplateLoading}
                    type="button"
                >
                    {t('cancel')}
                </Button>
                <Button data-testid="save-button" className={'flex-1'} variant={'solid'} isLoading={isUpdating} disabled={isUpdating} onClick={handleSaveChanges}>
                    {t('saveChanges', { ns: 'organization' })}
                </Button>
            </div>
        </div>
    );
}

export default AddDetailsToOrganizationWordListTemplate;
